/**
 * Job timeline — pure merge/group/filter logic for the job workspace's
 * merged, reverse-chronological stream.
 *
 * Since spec 08 (docs/features/job-workspace-redesign/
 * 08_views_module_update_spec.md), most of this stream is server-composed
 * and server-paginated: `GET /views/job-timeline/{job_no}` already merges
 * notes, Tharstern notes and production/delivery/invoice events into one
 * `UNION ALL`. Three sources stay client-merged on
 * purpose, matching the two-tier split the rest of the page follows:
 *
 * - emails/meetings — already have their own composed, unpaginated read
 *   (`GET /views/job-comms/{job_no}`, unaffected by spec 08 — see its §3
 *   "out of scope") that the interactive confirm/reject/relink/attach
 *   affordances also depend on having the *complete* set for, not one
 *   page of it.
 * - documents — a live external read (Tharstern attachments); folding it
 *   into the composed timeline read is exactly what spec 08 §6 forbids.
 *   `kind=document` on the timeline endpoint always returns an empty page.
 *
 * These three are still fetched client-side (unpaginated, already-bounded
 * reads) and merged in here — only on the timeline's first page, so they
 * don't repeat on every page of the five server-paginated kinds.
 */

import type * as API from "../data/api-types";
import type { JobEmailLink, MeetingLink } from "../data/adapter";

export type JobTimelineEntryKind = API.JobTimelineEntryKind;

/** One row in the merged stream, normalised from either a server
 *  `JobTimelineEntryView` or a client-merged email/meeting/document. */
export interface JobTimelineEntry {
  id: string;
  kind: JobTimelineEntryKind;
  occurredAt: string; // ISO
  displayText: string;
  /** True for a `tharstern_note` entry, and for a `note` entry mirrored to
   *  Tharstern (`note.tharstern_note_id != null`) — computed server-side
   *  (`is_tharstern_sourced`), not re-derived here. */
  isTharsternSourced: boolean;
  note?: API.Note | null;
  tharsternNote?: API.TimelineTharsternNoteView | null;
  email?: JobEmailLink | null;
  meeting?: MeetingLink | null;
  document?: API.TharsternAttachment;
}

// Kinds that read as "the system said something happened" rather than "a
// person said something" — grouped into a collapsed line when more than
// GROUP_THRESHOLD appear consecutively, so the default view reads as
// correspondence (see the product brief's "grouping consecutive system
// events is worth considering").
const SYSTEM_KINDS: ReadonlySet<JobTimelineEntryKind> = new Set([
  "production",
  "delivery",
  "invoice",
]);

export function isSystemTimelineEntry(entry: JobTimelineEntry): boolean {
  return SYSTEM_KINDS.has(entry.kind);
}

export type JobTimelineFilterId =
  | "all"
  | "notes"
  | "emails"
  | "meetings"
  | "documents"
  | "production"
  | "delivery"
  | "invoice";

export interface JobTimelineFilter {
  id: JobTimelineFilterId;
  label: string;
}

// Order matches the brief's filter-chip list verbatim.
export const JOB_TIMELINE_FILTERS: JobTimelineFilter[] = [
  { id: "all", label: "All" },
  { id: "notes", label: "Notes" },
  { id: "emails", label: "Emails" },
  { id: "meetings", label: "Meetings" },
  { id: "documents", label: "Documents" },
  { id: "production", label: "Production" },
  { id: "delivery", label: "Delivery" },
  { id: "invoice", label: "Invoice" },
];

const FILTER_KINDS: Record<JobTimelineFilterId, JobTimelineEntryKind[] | null> =
  {
    all: null,
    notes: ["note", "tharstern_note"],
    emails: ["email"],
    meetings: ["meeting"],
    documents: ["document"],
    production: ["production"],
    delivery: ["delivery"],
    invoice: ["invoice"],
  };

export function matchesJobTimelineFilter(
  entry: JobTimelineEntry,
  filter: JobTimelineFilterId,
): boolean {
  const kinds = FILTER_KINDS[filter];
  return kinds === null || kinds.includes(entry.kind);
}

/** The five kinds `GET /views/job-timeline/{job_no}` actually sources
 *  (spec 08 — the query never emits `document`; `email`/`meeting` are real
 *  arms there too, but this page keeps sourcing those from `getJobComms`
 *  instead — see the module docstring). Sent explicitly rather than
 *  omitting `kind` — the backend treats an omitted/empty `kind` as "every
 *  kind", which would pull `email`/`meeting` rows a second time alongside
 *  the ones already merged in from `getJobComms`. */
export const SERVER_TIMELINE_KINDS: JobTimelineEntryKind[] = [
  "note",
  "tharstern_note",
  "production",
  "delivery",
  "invoice",
];

/** The `kind` values to request from `listJobTimeline` for the current
 *  filter chip — the intersection of the filter's kinds with the
 *  server-sourced five. Empty means "this filter needs no server fetch"
 *  (emails/meetings/documents), which the caller must check before
 *  requesting — an empty `kind` list is not a safe stand-in for "none": the
 *  backend reads it as "every kind". */
export function serverKindsForFilter(
  filter: JobTimelineFilterId,
): JobTimelineEntryKind[] {
  const wanted = FILTER_KINDS[filter] ?? SERVER_TIMELINE_KINDS;
  return wanted.filter((kind) => SERVER_TIMELINE_KINDS.includes(kind));
}

function fromServerEntry(e: API.JobTimelineEntryView): JobTimelineEntry {
  // `email`/`meeting` are deliberately never populated here — this page
  // never requests those kinds from the server (see `SERVER_TIMELINE_KINDS`
  // and the module docstring); `fromEmail`/`fromMeeting` below build those
  // entries from `getJobComms` instead, in the shape this section's
  // interactive affordances already expect.
  return {
    id: e.id,
    kind: e.kind,
    occurredAt: e.occurred_at,
    displayText: e.display_text,
    isTharsternSourced: e.is_tharstern_sourced,
    note: e.note ?? null,
    tharsternNote: e.tharstern_note ?? null,
  };
}

function fromEmail(email: JobEmailLink): JobTimelineEntry {
  return {
    id: `email-${email.id}`,
    kind: "email",
    occurredAt: email.sent_at,
    displayText: email.subject,
    isTharsternSourced: false,
    email,
  };
}

function fromMeeting(meeting: MeetingLink): JobTimelineEntry {
  return {
    id: `meeting-${meeting.id}`,
    kind: "meeting",
    occurredAt: meeting.start_at,
    displayText: meeting.title,
    isTharsternSourced: false,
    meeting,
  };
}

function fromDocument(doc: API.TharsternAttachment): JobTimelineEntry {
  return {
    id: `doc-${doc.id}`,
    kind: "document",
    occurredAt: doc.created_at ?? "",
    displayText: doc.filename ?? "Untitled document",
    isTharsternSourced: false,
    document: doc,
  };
}

export interface JobTimelineMergeInput {
  /** The currently fetched server page — already filtered/sorted/paginated
   *  by `listJobTimeline`. Never re-filtered, re-sorted or re-sliced here. */
  serverEntries: API.JobTimelineEntryView[];
  emails: JobEmailLink[];
  meetings: MeetingLink[];
  documents: API.TharsternAttachment[];
  /** Only page 1 of the server-paginated kinds also carries
   *  emails/meetings/documents — they have no page of their own, so
   *  repeating them on every page would duplicate them. */
  includeLocalSources: boolean;
}

/** Merge the current server page with the client-merged sources. Pure —
 *  callers own fetching each source; this only normalises, merges and
 *  sorts what it's given. */
export function buildJobTimeline(
  input: JobTimelineMergeInput,
): JobTimelineEntry[] {
  const entries = input.serverEntries.map(fromServerEntry);
  if (input.includeLocalSources) {
    entries.push(...input.emails.map(fromEmail));
    entries.push(...input.meetings.map(fromMeeting));
    entries.push(...input.documents.map(fromDocument));
  }
  return entries
    .filter((e) => Boolean(e.occurredAt))
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

export type JobTimelineRow =
  | { type: "entry"; entry: JobTimelineEntry }
  | {
      type: "group";
      id: string;
      entries: JobTimelineEntry[];
      occurredAt: string;
    };

// More than this many consecutive system entries collapse into one
// expandable line.
const GROUP_THRESHOLD = 3;

/** Collapse runs of more than `GROUP_THRESHOLD` consecutive system entries
 *  (production/delivery/invoice) into a single expandable row.
 *  Person-authored entries (notes, emails, meetings, documents) are never
 *  grouped. */
export function groupConsecutiveSystemEntries(
  entries: JobTimelineEntry[],
): JobTimelineRow[] {
  const rows: JobTimelineRow[] = [];
  let run: JobTimelineEntry[] = [];

  const flushRun = () => {
    if (run.length === 0) return;
    if (run.length > GROUP_THRESHOLD) {
      rows.push({
        type: "group",
        id: `group-${run[0]!.id}`,
        entries: run,
        occurredAt: run[0]!.occurredAt,
      });
    } else {
      for (const entry of run) rows.push({ type: "entry", entry });
    }
    run = [];
  };

  for (const entry of entries) {
    if (isSystemTimelineEntry(entry)) {
      run.push(entry);
    } else {
      flushRun();
      rows.push({ type: "entry", entry });
    }
  }
  flushRun();
  return rows;
}
